import { Menu, Package } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ManagerBottomNavigationBar from '../../../components/ManagerBottomNavigationBar';
import ManagerPurchaseCard from '../../../components/ManagerPurchaseCard';
import SidebarMenu from '../../../components/SidebarMenu';
import useManagerPurchasesStore from '../../../store/managerPurchases';

export default function ManagerPurchasesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showSidebar, setShowSidebar] = useState(false);
  const {
    purchases,
    isLoading,
    error,
    fetchPurchases,
    approvePurchase,
    deletePurchase,
  } = useManagerPurchasesStore();

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const pendingPurchases = purchases.filter(p => p.status === 'Pending');
  const processedPurchases = purchases.filter(p => p.status !== 'Pending');

  return (
    <div className="bg-white min-vh-100 d-flex flex-column">
      <Container fluid className="flex-grow-1 pb-5">
        {/* Sticky Header */}
        <div
          className="sticky-top bg-white"
          style={{
            zIndex: 1020,
            borderBottom: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <Row className="align-items-center p-4 pb-2">
            <Col xs="auto">
              <Menu
                size={24}
                className="text-dark"
                onClick={() => setShowSidebar(true)}
                style={{ cursor: 'pointer' }}
              />
            </Col>
            <Col className="text-center">
              <h2 className="text-dark h5 fw-bold mb-0">
                {t('manager.purchases.title')}
              </h2>
            </Col>
            <Col xs="auto" style={{ width: '24px' }} />
          </Row>
        </div>

        {/* Content */}
        <div className="px-0 pt-3">
          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {!isLoading && !error && purchases.length === 0 && (
            <div className="text-center py-5">
              <div className="mb-4">
                <Package size={64} className="text-muted" />
              </div>
              <h5 className="text-muted mb-3">
                {t('manager.purchases.noPurchases')}
              </h5>
              <p className="text-muted">
                {t('manager.purchases.emptyStateDescription')}
              </p>
              <Button
                variant="primary"
                className="mt-3"
                onClick={() => navigate('/manager/home')}
              >
                {t('manager.purchases.backToHome')}
              </Button>
            </div>
          )}

          {!isLoading && !error && (
            <>
              {pendingPurchases.length > 0 && (
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">
                    {t('manager.purchases.pending')}
                  </h5>
                  {pendingPurchases.map(purchase => (
                    <ManagerPurchaseCard
                      key={purchase._id}
                      purchase={purchase}
                      onApprove={approvePurchase}
                      onDelete={deletePurchase}
                    />
                  ))}
                </div>
              )}

              {pendingPurchases.length === 0 &&
                processedPurchases.length > 0 && (
                  <div className="text-center py-4 mb-4">
                    <div className="mb-3">
                      <Package size={48} className="text-success" />
                    </div>
                    <h6 className="text-success mb-2">
                      {t('manager.purchases.allProcessed')}
                    </h6>
                    <p className="text-muted small">
                      {t('manager.purchases.noMorePending')}
                    </p>
                  </div>
                )}

              {processedPurchases.length > 0 && (
                <div className="mb-4">
                  <h5 className="fw-bold mb-3 text-muted">
                    {t('manager.purchases.processed')}
                  </h5>
                  <div style={{ opacity: 0.7 }}>
                    {processedPurchases.map(purchase => (
                      <ManagerPurchaseCard
                        key={purchase._id}
                        purchase={purchase}
                        onApprove={approvePurchase}
                        onDelete={deletePurchase}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </Container>

      <SidebarMenu show={showSidebar} onHide={() => setShowSidebar(false)} />
      <ManagerBottomNavigationBar />
    </div>
  );
}
